import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router"
import { HomeScreen } from "../pages/HomeScreen"
import { LoginScreen } from "../pages/LoginScreen"
import { ReaderScreen } from "../reader/ReaderScreen"
import { BottomTabBar } from "./BottomTabBar"
import { DesktopDrawer } from "./DesktopDrawer"
import { ProfileScreen } from "../pages/profile/ProfileScreen"
import { ManageStorageScreen } from "../pages/profile/manage-storage/ManageStorageScreen"
import { LibraryTopTabNavigator } from "../library/LibraryTopTabNavigator"
import { BookDetailsScreen } from "../books/details/BookDetailsScreen"
import { CollectionDetailsScreen } from "../pages/collections/CollectionDetailsScreen/CollectionDetailsScreen"
import { DataSourcesListScreen } from "../pages/sync/DataSourcesListScreen"
import { SearchScreen } from "../pages/SearchScreen"
import { SettingsScreen } from "../pages/SettingsScreen"
import { StatisticsScreen } from "../pages/StatisticsScreen"
import { ProblemsScreen } from "../problems/ProblemsScreen"
import { LibraryBooksScreen } from "../library/books/LibraryBooksScreen"
import { LibraryCollectionScreen } from "../pages/LibraryCollectionScreen"
import { LibraryTagsScreen } from "../pages/library/LibraryTagsScreen"
import { TagBooksScreen } from "../pages/library/tags/$id/books/TagBooksScreen"
import { CollectionBooksScreen } from "../pages/collections/$id/books/CollectionBooksScreen"
import { BookTagsScreen } from "../pages/books/$id/tags/BookTagsScreen"
import { BookCollectionsScreen } from "../pages/books/$id/collections/BookCollectionsScreen"
import { BookMetadataSourceScreen } from "../pages/books/$id/metadata/$source/BookMetadataSourceScreen"
import { BookOptimizeScreen } from "../pages/books/$id/optimize/BookOptimizeScreen"
import type { ReactNode } from "react"
import { useMediaQuery, useTheme } from "@mui/material"
import { SearchScreenExpanded } from "../search/SearchScreenExpanded"
import { useActiveProfile } from "../profiles"
import { DataSourcesTabNavigator } from "../dataSources/DataSourcesTabNavigator"
import { DataSourcesReportsScreen } from "../dataSources/reports/DataSourcesReportsScreen"
import { SecurityScreen } from "../pages/profile/SecurityScreen"
import { KoreaderSyncServerScreen } from "../integrations/koreader/server/KoreaderSyncServerScreen"
import { KoreaderProgressSyncScreen } from "../integrations/koreader/client/KoreaderProgressSyncScreen"
import { IntegrationsScreen } from "../integrations/IntegrationsScreen"
import { DropboxIntegrationScreen } from "../integrations/dropbox/DropboxIntegrationScreen"
import { GoogleIntegrationScreen } from "../integrations/google/GoogleIntegrationScreen"
import { KoreaderIntegrationScreen } from "../integrations/koreader/KoreaderIntegrationScreen"
import { OneDriveIntegrationScreen } from "../integrations/one-drive/OneDriveIntegrationScreen"
import { OpdsIntegrationScreen } from "../integrations/opds/OpdsIntegrationScreen"
import { ServerIntegrationScreen } from "../integrations/server/ServerIntegrationScreen"
import { SynologyDriveIntegrationScreen } from "../integrations/synology-drive/SynologyDriveIntegrationScreen"
import { WebdavIntegrationScreen } from "../integrations/webdav/WebdavIntegrationScreen"
import { ROUTES } from "./routes"
import { SignUpScreen } from "../pages/SignUpScreen"
import { SecretsScreen } from "../pages/profile/SecretsScreen"
import { NewDataSourceScreen } from "../pages/sync/NewDataSourceScreen"
import { DataSourceDetailsScreen } from "../pages/sync/DataSourceDetailsScreen"
import { AddConnectorScreen } from "../connectors/AddConnectorScreen"
import { EditConnectorScreen } from "../connectors/EditConnectorScreen"
import { plugins } from "../dataSources"
import { SignUpCompleteScreen } from "../pages/SignUpCompleteScreen"
import { MagicLinkCompleteScreen } from "../pages/MagicLinkCompleteScreen"
import { NotificationsScreen } from "../notifications/inbox/NotificationsScreen"
import { ReLoginScreen } from "../pages/ReloginScreen"
import { ModalHistoryProvider } from "./modalHistory"

const AppShell = ({ children }: { children: ReactNode }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  if (isDesktop) {
    return <DesktopDrawer>{children}</DesktopDrawer>
  }

  return <>{children}</>
}

const AppShellRouteWrapper = () => (
  <AppShell>
    <Outlet />
  </AppShell>
)

const MobileTabBar = ({ children }: { children: ReactNode }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  if (isDesktop) {
    return <>{children}</>
  }

  return <BottomTabBar>{children}</BottomTabBar>
}

const MobileTabBarRouteWrapper = () => (
  <MobileTabBar>
    <Outlet />
  </MobileTabBar>
)

export const AppBrowserRouter = ({ children }: { children: ReactNode }) => {
  const hasSession = !!useActiveProfile().data

  const content = (
    <ModalHistoryProvider>
      <Routes>
        <Route
          path={ROUTES.LOGIN_MAGIC_LINK}
          element={<MagicLinkCompleteScreen />}
        />
        <Route
          path={ROUTES.SIGN_UP_COMPLETE}
          element={<SignUpCompleteScreen />}
        />
        {hasSession ? (
          <>
            <Route path={ROUTES.READER} element={<ReaderScreen />} />
            <Route path="*" element={<AppShellRouteWrapper />}>
              <Route
                path={`${ROUTES.PROBLEMS.slice(1)}`}
                element={<ProblemsScreen />}
              />
              <Route
                path={ROUTES.BOOK_DETAILS.slice(1)}
                element={<BookDetailsScreen />}
              />
              <Route
                path={ROUTES.BOOK_TAGS.slice(1)}
                element={<BookTagsScreen />}
              />
              <Route
                path={ROUTES.BOOK_COLLECTIONS.slice(1)}
                element={<BookCollectionsScreen />}
              />
              <Route
                path={ROUTES.BOOK_OPTIMIZE.slice(1)}
                element={<BookOptimizeScreen />}
              />
              <Route
                path={ROUTES.BOOK_METADATA_SOURCE.slice(1)}
                element={<BookMetadataSourceScreen />}
              />
              <Route
                path={ROUTES.COLLECTION_DETAILS.slice(1)}
                element={<CollectionDetailsScreen />}
              />
              <Route
                path={ROUTES.TAG_BOOKS.slice(1)}
                element={<TagBooksScreen />}
              />
              <Route
                path={ROUTES.COLLECTION_BOOKS.slice(1)}
                element={<CollectionBooksScreen />}
              />
              <Route path={ROUTES.SEARCH.slice(1)}>
                <Route index element={<SearchScreen />} />
                <Route
                  path=":search/:type"
                  element={<SearchScreenExpanded />}
                />
              </Route>
              <Route
                path={ROUTES.MANAGE_STORAGE.slice(1)}
                element={<ManageStorageScreen />}
              />
              <Route
                path={ROUTES.NOTIFICATIONS.slice(1)}
                element={<NotificationsScreen />}
              />
              <Route
                path={ROUTES.SESSION_EXPIRED.slice(1)}
                element={<ReLoginScreen />}
              />
              <Route
                path={ROUTES.SECURITY.slice(1)}
                element={<SecurityScreen />}
              />
              <Route
                path={ROUTES.SECRETS.slice(1)}
                element={<SecretsScreen />}
              />
              <Route
                path={ROUTES.KOREADER_SYNC_SERVER.slice(1)}
                element={<KoreaderSyncServerScreen />}
              />
              <Route
                path={ROUTES.KOREADER_PROGRESS_SYNC.slice(1)}
                element={<KoreaderProgressSyncScreen />}
              />
              <Route
                path={ROUTES.SETTINGS.slice(1)}
                element={<SettingsScreen />}
              />
              <Route
                path={ROUTES.STATISTICS.slice(1)}
                element={<StatisticsScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_DROPBOX.slice(1)}
                element={<DropboxIntegrationScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_GOOGLE.slice(1)}
                element={<GoogleIntegrationScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_KOREADER.slice(1)}
                element={<KoreaderIntegrationScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_ONE_DRIVE.slice(1)}
                element={<OneDriveIntegrationScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_OPDS.slice(1)}
                element={<OpdsIntegrationScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_SERVER.slice(1)}
                element={<ServerIntegrationScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_SYNOLOGY_DRIVE.slice(1)}
                element={<SynologyDriveIntegrationScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_WEBDAV.slice(1)}
                element={<WebdavIntegrationScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_CONNECTORS_NEW.slice(1)}
                element={<AddConnectorScreen />}
              />
              <Route
                path={ROUTES.INTEGRATION_CONNECTORS_EDIT.slice(1)}
                element={<EditConnectorScreen />}
              />
              <Route
                path={ROUTES.SYNC_NEW_DATASOURCES.slice(1)}
                element={<NewDataSourceScreen />}
              />
              <Route
                path={ROUTES.DATASOURCE_DETAILS.slice(1)}
                element={<DataSourceDetailsScreen />}
              />
              <Route path="*" element={<MobileTabBarRouteWrapper />}>
                <Route index element={<HomeScreen />} />
                <Route path="profile" element={<ProfileScreen />} />
                <Route path="integrations" element={<IntegrationsScreen />} />
                <Route path="library" element={<LibraryTopTabNavigator />}>
                  <Route index element={<Navigate to="books" replace />} />
                  <Route path="books" element={<LibraryBooksScreen />} />
                  <Route
                    path="collections"
                    element={<LibraryCollectionScreen />}
                  />
                  <Route path="tags" element={<LibraryTagsScreen />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
                <Route path="sync" element={<DataSourcesTabNavigator />}>
                  <Route
                    index
                    element={<Navigate to="datasources" replace />}
                  />
                  <Route
                    path="datasources"
                    element={<DataSourcesListScreen />}
                  />
                  <Route
                    path="reports"
                    element={<DataSourcesReportsScreen />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to={ROUTES.SYNC_DATASOURCES} replace />}
                  />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Route>
          </>
        ) : (
          <>
            <Route path={ROUTES.LOGIN} element={<LoginScreen />} />
            <Route path={ROUTES.SIGN_UP} element={<SignUpScreen />} />
            <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
          </>
        )}
      </Routes>
      {children}
    </ModalHistoryProvider>
  )

  return (
    <BrowserRouter>
      {plugins.reduce((Comp, { Provider }, index) => {
        if (Provider) {
          return <Provider key={index}>{Comp}</Provider>
        }
        return Comp
      }, content)}
    </BrowserRouter>
  )
}
